/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import Link from '@jira/platform__link-with-analytics/src/LinkWithAnalytics.tsx';
import {
	SERVICE_DESK_PROJECT,
	type ProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { TRASH_AND_RESTORE_HELP_LINK } from '../../common/constants.tsx';

const explainWhatProjectRestoreDoesPart1 = defineMessage({
	id: 'project-restore-modal.modal-message.explain-what-project-restore-does-part-1',
	defaultMessage:
		'The project along with its issues, Jira components, attachments, and versions will be restored.',
	description: 'Explanation for restoring a project',
});

const jsmExplainWhatProjectRestoreDoes = defineMessage({
	id: 'project-restore-modal.modal-message.jsm-explain-what-project-restore-does',
	defaultMessage:
		'Once restored, this service project will appear in directories and its issues will appear in search results. You may need to turn on email support for this service project to work as normal.',
	description: 'Explanation for restoring a project',
});

const jsmLearnMoreAboutTrashAndRestore = defineMessage({
	id: 'project-restore-modal.modal-message.jsm-learn-more-about-trash-and-restore',
	defaultMessage: 'Learn more about trash and restore.',
	description: 'Link to In project help (IPH) to learn more about trash and restore',
});

const explainWhatProjectRestoreDoesPart1IssueTermRefresh = defineMessage({
	id: 'project-restore-modal.modal-message.explain-what-project-restore-does-part-1-issue-term-refresh',
	defaultMessage:
		'The project along with its work items, Jira components, attachments, and versions will be restored',
	description: 'Explanation for restoring a project',
});

const jsmExplainWhatProjectRestoreDoesIssueTermRefresh = defineMessage({
	id: 'project-restore-modal.modal-message.jsm-explain-what-project-restore-does-issue-term-refresh',
	defaultMessage:
		'Once restored, this service project will appear in directories and its work items will appear in search results. You may need to turn on email support for this service project to work as normal.',
	description: 'Explanation for restoring a project',
});

const explainWhatProjectRestoreDoesPart1IssueTermRefreshGalaxiaMessage = defineMessage({
	id: 'project-restore-modal.modal-message.explain-what-project-restore-does-part-1-issue-term-refresh-galaxia',
	defaultMessage:
		'The space along with its work items, Jira components, attachments, and versions will be restored',
	description: 'Explanation for restoring a project',
});

const jsmExplainWhatProjectRestoreDoesIssueTermRefreshGalaxiaMessage = defineMessage({
	id: 'project-restore-modal.modal-message.jsm-explain-what-project-restore-does-issue-term-refresh-galaxia',
	defaultMessage:
		'Once restored, this service space will appear in directories and its work items will appear in search results. You may need to turn on email support for this service space to work as normal.',
	description: 'Explanation for restoring a project',
});

type Props = {
	projectType: ProjectType | undefined;
};

const isServiceDesk = (projectType?: ProjectType) => projectType === SERVICE_DESK_PROJECT;

export const ModalText = ({ projectType }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const getMessage = () => {
		const jsmExplainWhatProjectRestoreDoesIssueTermRefreshGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? jsmExplainWhatProjectRestoreDoesIssueTermRefreshGalaxiaMessage
			: jsmExplainWhatProjectRestoreDoesIssueTermRefresh;
		const explainWhatProjectRestoreDoesPart1IssueTermRefreshGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? explainWhatProjectRestoreDoesPart1IssueTermRefreshGalaxiaMessage
			: explainWhatProjectRestoreDoesPart1IssueTermRefresh;
		let message;

		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			message = isServiceDesk(projectType)
				? jsmExplainWhatProjectRestoreDoesIssueTermRefreshGalaxia
				: explainWhatProjectRestoreDoesPart1IssueTermRefreshGalaxia;
		} else {
			message = isServiceDesk(projectType)
				? jsmExplainWhatProjectRestoreDoes
				: explainWhatProjectRestoreDoesPart1;
		}

		return message;
	};

	const formattedMessage = formatMessage(getMessage());

	return <span>{formattedMessage}&nbsp;</span>;
};

export const LearnMoreLink = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Link href={TRASH_AND_RESTORE_HELP_LINK} target="_blank">
			{formatMessage(jsmLearnMoreAboutTrashAndRestore)}
		</Link>
	);
};

export const ModalMessageContent = ({ projectType }: Props): React.JSX.Element => (
	<>
		<ModalText projectType={projectType} />
		{isServiceDesk(projectType) && <LearnMoreLink />}
	</>
);
